import React, { useState, useMemo } from 'react';
import { FaSearch, FaPlus } from 'react-icons/fa';

const PRESET_CATEGORIES = [
    'Income & Salary',
    'Income & Deposits',
    'Rent & Housing',
    'Utilities & Bills',
    'Groceries',
    'Dining & Food',
    'Travel & Fuel',
    'Shopping & Retail',
    'Software & Subscriptions',
    'Bank Fees & Charges',
    'Transfers & Payments',
    'Medical & Health',
    'Office Supplies',
    'General Expense'
];

const TransactionTable = ({ transactions = [], onUpdateTransactions, setTransactions }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');

    const updateTxList = (updater) => {
        if (onUpdateTransactions) {
            onUpdateTransactions(updater(transactions));
        } else if (setTransactions) {
            setTransactions(updater);
        }
    };

    const handleToggle = (id) => {
        updateTxList(prev => prev.map(tx => 
            tx.id === id ? { ...tx, enabled: tx.enabled === false } : tx
        ));
    };

    const handleIncludeAll = () => {
        updateTxList(prev => prev.map(tx => ({ ...tx, enabled: true })));
    };

    const handleExcludeAll = () => {
        updateTxList(prev => prev.map(tx => ({ ...tx, enabled: false })));
    };

    const handleAddEntry = () => {
        const newId = `tx_${Date.now()}`;
        const newTx = {
            id: newId,
            date: new Date().toISOString().split('T')[0],
            description: 'New Transaction',
            category: 'General Expense',
            type: 'Debit',
            amount: 0.00,
            enabled: true
        };
        updateTxList(prev => [newTx, ...prev]);
    };

    const handleRowChange = (id, field, value) => {
        updateTxList(prev => prev.map(tx => 
            tx.id === id ? { ...tx, [field]: field === 'amount' ? (parseFloat(value) || 0) : value } : tx
        ));
    };

    const categories = useMemo(() => {
        const txCats = transactions.map(tx => tx.category).filter(Boolean);
        const combined = new Set([...PRESET_CATEGORIES, ...txCats]);
        return ['All Categories', ...Array.from(combined)];
    }, [transactions]);

    const filteredTransactions = useMemo(() => {
        return transactions
            .slice() // Create a shallow copy to avoid mutating the original array
            .sort((a, b) => {
                const dateA = a.date ? new Date(a.date) : null;
                const dateB = b.date ? new Date(b.date) : null;

                if (!dateA || isNaN(dateA)) return 1; // push invalid dates to the end
                if (!dateB || isNaN(dateB)) return -1;
                return dateA - dateB;
            }) // Sort by date ascending
            .filter(tx => {
                const term = searchTerm.toLowerCase();
                const matchesSearch = (tx.description || '').toLowerCase().includes(term) ||
                                      (tx.category || '').toLowerCase().includes(term) ||
                                      (tx.date || '').toLowerCase().includes(term);
                
                const matchesType = filterType === 'All' || tx.type === filterType;
                const matchesCategory = filterCategory === 'All' || filterCategory === 'All Categories' || tx.category === filterCategory;
                
                let matchesStatus = true;
                if (filterStatus === 'Included') matchesStatus = tx.enabled !== false;
                if (filterStatus === 'Excluded') matchesStatus = tx.enabled === false;
    
                return matchesSearch && matchesType && matchesCategory && matchesStatus;
            });
    }, [transactions, searchTerm, filterType, filterCategory, filterStatus]);

    const summary = useMemo(() => {
        let totalDebit = 0;
        let totalCredit = 0;
        let debitCount = 0;
        let creditCount = 0;
        let includedCount = 0;

        transactions.forEach(tx => {
            if (tx.enabled !== false) {
                includedCount++;
                const amt = parseFloat(tx.amount) || 0;
                if (tx.type === 'Debit') {
                    totalDebit += amt;
                    debitCount++;
                } else {
                    totalCredit += amt;
                    creditCount++;
                }
            }
        });

        const net = totalCredit - totalDebit;

        return {
            total: transactions.length,
            included: includedCount,
            debit: totalDebit,
            credit: totalCredit,
            debitCount,
            creditCount,
            net: net
        };
    }, [transactions]);

    return (
        <div className="bank-statement-card mb-3">
            {/* Summary Metrics Row matching screenshot */}
            <div className="summary-cards-row mb-4">
                <div className="summary-card-item">
                    <div className="summary-label">INCLUDED ITEMS</div>
                    <div className="summary-value text-dark">{summary.included} / {summary.total}</div>
                </div>
                <div className="summary-card-item">
                    <div className="summary-label">TOTAL DEBITS ({summary.debitCount})</div>
                    <div className="summary-value text-danger">${summary.debit.toFixed(2)}</div>
                </div>
                <div className="summary-card-item">
                    <div className="summary-label">TOTAL CREDITS ({summary.creditCount})</div>
                    <div className="summary-value text-success">${summary.credit.toFixed(2)}</div>
                </div>
                {/* <div className="summary-card-item">
                    <div className="summary-label">NET BALANCE</div>
                    <div className={`summary-value ${summary.net < 0 ? 'text-danger' : 'text-success'}`}>
                        ${summary.net < 0 ? `-${Math.abs(summary.net).toFixed(2)}` : summary.net.toFixed(2)}
                    </div>
                </div> */}
            </div>

            {/* Controls Bar matching screenshot */}
            <div className="table-controls-bar mb-3">
                <div className="search-box-wrapper">
                    <FaSearch className="search-icon" />
                    <input 
                        type="text" 
                        className="search-input"
                        placeholder="Search description,category,date..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="controls-right">
                    <select 
                        className="control-select"
                        value={filterCategory} 
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>

                    <select 
                        className="control-select"
                        value={filterType} 
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="All">All Types</option>
                        <option value="Debit">Debit</option>
                        <option value="Credit">Credit</option>
                    </select>

                    <select 
                        className="control-select"
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="All">All Statuses</option>
                        <option value="Included">Included</option>
                        <option value="Excluded">Excluded</option>
                    </select>

                    <button className="btn-pill-outline" onClick={handleIncludeAll} title="Include All">
                        Include All
                    </button>
                    <button className="btn-pill-outline" onClick={handleExcludeAll} title="Exclude All">
                        Exclude All
                    </button>
                    <button className="btn-pill-blue" onClick={handleAddEntry}>
                        <FaPlus size={11} className="me-1" /> Add Entry
                    </button>
                </div>
            </div>

            {/* Datalist for Category Suggestions */}
            <datalist id="category-options-list">
                {PRESET_CATEGORIES.map(cat => (
                    <option key={cat} value={cat} />
                ))}
            </datalist>

            {/* Transaction Table */}
            <div className="table-responsive border-0">
                <table className="transaction-table-custom">
                    <thead>
                        <tr>
                            <th style={{ width: '130px' }}>DATE</th>
                            <th>DESCRIPTION</th>
                            <th style={{ width: '170px' }}>CATEGORY</th>
                            <th style={{ width: '110px' }}>TYPE</th>
                            <th style={{ width: '120px' }}>AMOUNT ($)</th>
                            <th style={{ width: '90px', textAlign: 'right' }}>INCLUDE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTransactions.map(tx => {
                            const isEnabled = tx.enabled !== false;

                            return (
                                <tr key={tx.id} className={!isEnabled ? 'excluded-row' : ''}>
                                    <td className="editable-cell">
                                        <input 
                                            type="text" 
                                            name="date"
                                            className="table-input-field font-monospace small" 
                                            value={tx.date || ''} 
                                            onChange={(e) => handleRowChange(tx.id, 'date', e.target.value)} 
                                            placeholder="YYYY-MM-DD"
                                        />
                                    </td>
                                    <td className="editable-cell">
                                        <input 
                                            type="text" 
                                            name="description"
                                            className="table-input-field fw-medium" 
                                            value={tx.description || ''} 
                                            onChange={(e) => handleRowChange(tx.id, 'description', e.target.value)} 
                                            placeholder="Description..."
                                        />
                                    </td>
                                    <td className="editable-cell">
                                        <input 
                                            type="text" 
                                            list="category-options-list" 
                                            name="category"
                                            className="table-input-field" 
                                            value={tx.category || ''} 
                                            onChange={(e) => handleRowChange(tx.id, 'category', e.target.value)} 
                                            placeholder="Select/type..."
                                        />
                                    </td>
                                    <td className="editable-cell">
                                        <select 
                                            name="type"
                                            className="table-input-field" 
                                            value={tx.type || 'Debit'} 
                                            onChange={(e) => handleRowChange(tx.id, 'type', e.target.value)}
                                        >
                                            <option value="Debit">Debit</option>
                                            <option value="Credit">Credit</option>
                                        </select>
                                    </td>
                                    <td className="editable-cell">
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            name="amount"
                                            className="table-input-field fw-bold" 
                                            value={tx.amount ?? 0} 
                                            onChange={(e) => handleRowChange(tx.id, 'amount', e.target.value)} 
                                        />
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center justify-content-end gap-1">
                                            <label className="toggle-switch">
                                                <input 
                                                    type="checkbox" 
                                                    checked={isEnabled} 
                                                    onChange={() => handleToggle(tx.id)} 
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status-text">{isEnabled ? 'ON' : 'OFF'}</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredTransactions.length === 0 && (
                            <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">No transactions found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TransactionTable;